import { FormControlLabel, Switch } from '@mui/material';
import { useMemo, useState } from 'react';
import { EmptyState } from 'src/components/molecules';
import { DynamicQuestion } from 'src/components/molecules/DynamicQuestion/DynamicQuestion';
import { DynamicQuestionAnswerReadOnlyFallback } from 'src/components/molecules/DynamicQuestion/DynamicQuestionAnswerReadOnlyFallback';
import {
  DisclosureCaseByIdQuery,
  useGetQuestionnaireQuestionsQuery,
} from 'src/graphql';
import { CaseTypeEnum } from 'src/types';
import { buildOptionsByQuestionId } from 'src/utils/formatDynamicQuestionAnswerDisplay';
import {
  buildDynamicQuestionsByQuestionId,
  mergeQuestionnaireRowsForReadOnlyDisplay,
  normalizeDynamicQuestionAnswerValue,
} from 'src/utils/normalizeDynamicQuestionAnswerValue';
import { EmptyStateContainer } from './CoiDisclosures.styles';
import {
  DisclosureResponsesList,
  DisclosureResponsesSectionContainer,
  ResponseSectionHeading,
  ResponseSectionHeadingTitle,
} from './DisclosureResponsesSection.styles';

type DisclosureData = DisclosureCaseByIdQuery['disclosureCaseById'];

interface DisclosureResponsesSectionProps {
  disclosureData?: DisclosureData | null;
  isError?: boolean;
  reportId?: string;
  caseType: CaseTypeEnum;
}

export const DisclosureResponsesSection = ({
  disclosureData,
  isError,
  reportId = '',
  caseType,
}: DisclosureResponsesSectionProps) => {
  const [isFlaggedOnly, setIsFlaggedOnly] = useState(false);
  const questionnaireId = disclosureData?.questionnaireId ?? '';
  const { data: questionsData } = useGetQuestionnaireQuestionsQuery(
    { questionnaireId },
    { enabled: Boolean(questionnaireId) },
  );
  const optionsByQuestionId = useMemo(
    () =>
      buildOptionsByQuestionId(
        questionsData?.questionnaireQuestions?.questions ?? undefined,
      ),
    [questionsData?.questionnaireQuestions?.questions],
  );
  const questionsByQuestionId = useMemo(
    () =>
      buildDynamicQuestionsByQuestionId(
        questionsData?.questionnaireQuestions?.questions,
      ),
    [questionsData?.questionnaireQuestions?.questions],
  );
  const readOnlyRows = useMemo(
    () =>
      mergeQuestionnaireRowsForReadOnlyDisplay(
        questionsData?.questionnaireQuestions?.questions,
        disclosureData?.answers,
      ),
    [questionsData?.questionnaireQuestions?.questions, disclosureData?.answers],
  );

  const displayedRows = useMemo(
    () =>
      isFlaggedOnly
        ? readOnlyRows.filter(
            ({ answerValue }) =>
              normalizeDynamicQuestionAnswerValue(answerValue).isFlagged,
          )
        : readOnlyRows,
    [readOnlyRows, isFlaggedOnly],
  );

  if (!disclosureData && !isError) {
    return null;
  }

  if (isError) {
    return (
      <EmptyStateContainer>
        <EmptyState
          data-testid="disclosure-responses-section-empty-state"
          size="default"
          title="Error loading response details."
          description={'There was an issue retrieving your response details.'}
          renderIconButton={false}
        />
      </EmptyStateContainer>
    );
  }

  if (!disclosureData) {
    return (
      <EmptyStateContainer>
        <EmptyState
          data-testid="disclosure-responses-section-empty-state"
          size="default"
          title="No response data available."
          description="There is no response data available for this disclosure."
          renderIconButton={false}
        />
      </EmptyStateContainer>
    );
  }

  const pageTitle = caseType === CaseTypeEnum.COI ? 'Disclosure' : 'Misconduct';

  return (
    <DisclosureResponsesSectionContainer data-testid="disclosure-responses-section">
      {readOnlyRows.length > 0 && (
        <>
          <ResponseSectionHeading>
            <ResponseSectionHeadingTitle>
              {pageTitle} Responses
            </ResponseSectionHeadingTitle>
            {caseType === CaseTypeEnum.COI && (
              <FormControlLabel
                control={
                  <Switch
                    slotProps={{
                      input: { 'aria-label': 'Toggle flagged answers' },
                    }}
                    onChange={() => setIsFlaggedOnly((prev) => !prev)}
                  />
                }
                label="Display flagged answers only"
              />
            )}
          </ResponseSectionHeading>
          <DisclosureResponsesList data-testid="disclosure-responses-list">
            {displayedRows.map(({ question, answerValue }, index) => {
              const questionId = question.id ?? '';
              const normalizedAnswer =
                normalizeDynamicQuestionAnswerValue(answerValue);
              const resolvedQuestion =
                questionsByQuestionId[questionId] ?? question;
              const typeKey = resolvedQuestion?.questionType?.typeKey;
              const useDynamicQuestion =
                resolvedQuestion != null &&
                typeKey != null &&
                String(typeKey).trim() !== '';
              const dataTestId = `disclosure-response-answer-${index}`;
              if (useDynamicQuestion) {
                return (
                  <DynamicQuestion
                    key={questionId !== '' ? questionId : index}
                    readOnly
                    question={resolvedQuestion}
                    value={normalizedAnswer}
                    reportIdForFileDownload={reportId !== '' ? reportId : null}
                    fileDownloadCaseManagerView
                    onChange={() => {}}
                    data-testid={dataTestId}
                    isFlagged={normalizedAnswer.isFlagged}
                  />
                );
              }
              return (
                <DynamicQuestionAnswerReadOnlyFallback
                  key={questionId !== '' ? questionId : index}
                  questionText={resolvedQuestion.questionText ?? ''}
                  description={resolvedQuestion.description}
                  answerValue={answerValue}
                  formatOptions={optionsByQuestionId[questionId] ?? undefined}
                  reportId={reportId}
                  fileDownloadCaseManagerView
                  data-testid={dataTestId}
                  isFlagged={normalizedAnswer.isFlagged}
                />
              );
            })}
          </DisclosureResponsesList>
        </>
      )}
    </DisclosureResponsesSectionContainer>
  );
};
